import * as Dialog from "@radix-ui/react-dialog";
import {
  TbX,
  TbUser,
  TbCamera,
  TbUsers,
  TbLock,
  TbLoader2,
} from "react-icons/tb";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { supabase } from "@/supabase/supabaseClient";
import { useState, useEffect, useRef } from "react";
import { TeamService } from "@/services/teamService";
import { FileUploadService } from "@/services/fileUploadService";
import { SupabaseImageService } from "@/services/supabaseImageService";
import type { Team } from "@/types/task";
import toast from "react-hot-toast";

interface ProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { user, userProfile, refreshAuth } = useAuth();
  const { tenantId } = useTenant();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && user) {
      setFirstName(user.user_metadata?.first_name || "");
      setLastName(user.user_metadata?.last_name || "");
      setAvatarPreview(null);
      setAvatarFile(null);

      // Resolve avatar path to signed URL
      const avatarPath = userProfile?.avatar_url;
      if (avatarPath) {
        SupabaseImageService.getSignedUrl(avatarPath).then((url) => {
          setAvatarUrl(url);
        });
      } else {
        setAvatarUrl(null);
      }

      // Fetch user's teams
      if (tenantId && user.id) {
        setTeamsLoading(true);
        TeamService.getUserTeams(user.id, tenantId)
          .then((data) => setTeams(data))
          .catch((err) => {
            console.error("Error fetching user teams:", err);
            setTeams([]);
          })
          .finally(() => setTeamsLoading(false));
      }
    }
  }, [open, user, userProfile, tenantId]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setAvatarFile(file);
    const preview = await FileUploadService.createImagePreview(file);
    setAvatarPreview(preview);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);

    try {
      // Use the existing stored path, or upload a new one
      let avatarPath = userProfile?.avatar_url || null;

      // Upload avatar if changed
      if (avatarFile) {
        const result = await FileUploadService.uploadFile(
          avatarFile,
          `avatars/${user.id}`
        );
        avatarPath = result.path;
      }

      // Update Supabase auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: { first_name: firstName, last_name: lastName },
      });

      if (authError) throw authError;

      // Update user_profiles table via RPC (POST avoids CORS issues with PATCH)
      const { error: profileError } = await supabase.rpc(
        "update_user_profile",
        {
          p_user_id: user.id,
          p_first_name: firstName,
          p_last_name: lastName,
          p_avatar_url: avatarPath,
        }
      );

      if (profileError) {
        console.error("Error updating user_profiles:", profileError);
      }

      // Refresh auth context to reflect changes
      await refreshAuth();

      toast.success("Profile updated successfully");
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setResettingPassword(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email);

      if (error) throw error;

      toast.success("Password reset email sent. Check your inbox.");
    } catch (error) {
      console.error("Error sending password reset:", error);
      toast.error("Failed to send password reset email");
    } finally {
      setResettingPassword(false);
    }
  };

  const displayAvatar = avatarPreview || avatarUrl;
  const initials = (
    (firstName?.[0] || user?.email?.[0] || "U") +
    (lastName?.[0] || "")
  ).toUpperCase();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-gray-800/50 z-[1100]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-md z-[1101] max-h-[90vh] overflow-y-auto">
          <div className="flex flex-col gap-6 items-start justify-start p-6 w-full">
            {/* Header */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <TbUser size={20} className="text-blue-600" />
                </div>
                <Dialog.Title className="font-['Open_Sans'] font-bold text-[#444444] text-[18px]">
                  Edit Profile
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button className="cursor-pointer w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                  <TbX size={16} className="text-gray-500" />
                </button>
              </Dialog.Close>
            </div>

            {/* Avatar Section */}
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="relative">
                {displayAvatar ? (
                  <img
                    src={displayAvatar}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[var(--marian-blue)] flex items-center justify-center text-white font-bold text-[24px]">
                    {initials}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full border-2 border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <TbCamera size={14} className="text-gray-600" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>
              <p className="text-[12px] text-[#828282]">
                Click the camera icon to update your photo
              </p>
            </div>

            {/* Form */}
            <div className="flex flex-col gap-4 w-full">
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-semibold text-[#828282]">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[14px] text-[#444444] bg-gray-50 cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-semibold text-[#828282]">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[14px] text-[#444444] focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-semibold text-[#828282]">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[14px] text-[#444444] focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>

              {/* Teams Section */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <TbUsers size={16} className="text-[#828282]" />
                  <label className="text-[12px] font-semibold text-[#828282]">
                    My Teams
                  </label>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {teamsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <TbLoader2
                        size={18}
                        className="text-[#828282] animate-spin"
                      />
                      <span className="ml-2 text-[13px] text-[#828282]">
                        Loading teams...
                      </span>
                    </div>
                  ) : teams.length > 0 ? (
                    <ul className="divide-y divide-gray-100">
                      {teams.map((team) => (
                        <li
                          key={team.id}
                          className="px-3 py-2.5 flex items-center gap-2"
                        >
                          <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                            <TbUsers size={14} className="text-blue-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-[#444444] truncate">
                              {team.name}
                            </p>
                            {team.description && (
                              <p className="text-[11px] text-[#828282] truncate">
                                {team.description}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-3 py-4 text-[13px] text-[#828282] text-center">
                      Not assigned to any teams
                    </p>
                  )}
                </div>
              </div>

              {/* Reset Password */}
              <div className="flex flex-col gap-2 pt-1">
                <div className="flex items-center gap-2">
                  <TbLock size={16} className="text-[#828282]" />
                  <label className="text-[12px] font-semibold text-[#828282]">
                    Password
                  </label>
                </div>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="cursor-pointer w-full px-3 py-2.5 rounded-lg border border-gray-200 text-[13px] font-medium text-[#444444] hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {resettingPassword ? (
                    <>
                      <TbLoader2 size={14} className="animate-spin" />
                      Sending reset email...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
                <p className="text-[11px] text-[#828282]">
                  A password reset link will be sent to your email
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Dialog.Close asChild>
                  <button className="cursor-pointer flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-[14px] font-semibold text-[#444444] hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="cursor-pointer flex-1 px-4 py-2.5 rounded-lg bg-[var(--marian-blue)] text-white text-[14px] font-semibold hover:bg-[var(--marian-blue-dark)] transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
